/**
 * Frontend cadence helpers for the Scheduler tab (SCHEDULER.md §7b).
 *
 * Mirrors the backend ``schedule_cadence.py`` shape. The UI works in "presets"
 * (Hourly / Daily / Weekdays / Weekly / Monthly / Once / Custom) and turns the
 * user's choices into the ``cadence`` object the API expects:
 *
 *   { type: 'CRON', minute, hour, dayOfMonth, month, dayOfWeek, timezone }
 *   { type: 'RATE', value, unit }
 *   { type: 'ONCE', at, timezone }
 *
 * It also humanizes a returned cadence ("Mon–Fri 9:00 AM") for the job-card list
 * — cron is never shown raw in the headline (only in a tooltip).
 */

export const PRESETS = ['Now', 'Hourly', 'Daily', 'Weekdays', 'Weekly', 'Monthly', 'Once', 'Custom'];

export const WEEKDAYS = [
  { key: 'MON', label: 'Mon' },
  { key: 'TUE', label: 'Tue' },
  { key: 'WED', label: 'Wed' },
  { key: 'THU', label: 'Thu' },
  { key: 'FRI', label: 'Fri' },
  { key: 'SAT', label: 'Sat' },
  { key: 'SUN', label: 'Sun' },
];

export function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Split "HH:MM" into [hour, minute] strings (defaults 09:00). */
function splitTime(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim());
  if (!m) return ['9', '0'];
  return [String(parseInt(m[1], 10)), String(parseInt(m[2], 10))];
}

/**
 * Build the API ``cadence`` object from preset + fields.
 * @param {object} opts { preset, time:'HH:MM', weekdays:[keys], dayOfMonth, at:'ISO-local', cron, timezone }
 */
export function buildCadence(opts = {}) {
  const tz = opts.timezone || browserTimezone();
  const preset = opts.preset || 'Daily';
  const [hour, minute] = splitTime(opts.time);

  switch (preset) {
    case 'Hourly':
      return { type: 'RATE', value: 1, unit: 'hours' };
    case 'Daily':
      return { type: 'CRON', minute, hour, dayOfWeek: '*', timezone: tz };
    case 'Weekdays':
      return { type: 'CRON', minute, hour, dayOfWeek: 'MON-FRI', timezone: tz };
    case 'Weekly': {
      const days = (opts.weekdays && opts.weekdays.length ? opts.weekdays : ['MON']).join(',');
      return { type: 'CRON', minute, hour, dayOfWeek: days, timezone: tz };
    }
    case 'Monthly': {
      const dom = String(opts.dayOfMonth || 1);
      return { type: 'CRON', minute, hour, dayOfMonth: dom, timezone: tz };
    }
    case 'Once':
      return { type: 'ONCE', at: String(opts.at || ''), timezone: tz };
    case 'Custom':
      return { type: 'CRON', expr: String(opts.cron || ''), timezone: tz };
    default:
      return { type: 'CRON', minute, hour, dayOfWeek: '*', timezone: tz };
  }
}

function fmtTime(hour, minute) {
  const h = parseInt(hour, 10);
  const m = parseInt(minute, 10);
  if (Number.isNaN(h)) return '';
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const mm = Number.isNaN(m) ? '00' : String(m).padStart(2, '0');
  return `${h12}:${mm} ${ampm}`;
}

const _DOW_LABEL = {
  MON: 'Mon', TUE: 'Tue', WED: 'Wed', THU: 'Thu', FRI: 'Fri', SAT: 'Sat', SUN: 'Sun',
  'MON-FRI': 'Mon–Fri',
};

/**
 * Human summary of a returned cadence ({type, expr, timezone}) for the card
 * headline. Falls back to the raw expr if it can't be parsed.
 */
export function humanizeCadence(cadence) {
  if (!cadence || typeof cadence !== 'object') return '';
  const type = String(cadence.type || '').toUpperCase();
  const expr = String(cadence.expr || '');
  const tz = cadence.timezone ? ` ${cadence.timezone}` : '';

  if (type === 'RATE') {
    // rate(1 hour) / rate(6 hours)
    const m = /^rate\((\d+)\s+(\w+)\)$/.exec(expr);
    if (m) return `Every ${m[1] === '1' ? '' : m[1] + ' '}${m[2]}`.replace('  ', ' ').trim();
    return expr;
  }
  if (type === 'ONCE') {
    const m = /^at\((.+)\)$/.exec(expr);
    return m ? `Once · ${m[1].replace('T', ' ')}${tz}` : expr;
  }
  if (type === 'CRON') {
    // cron(min hour dom month dow year)
    const m = /^cron\((\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\)$/.exec(expr);
    if (!m) return expr;
    const [, minute, hour, dom, , dow] = m;
    const time = fmtTime(hour, minute);
    if (dom !== '?' && dom !== '*') return `Monthly · day ${dom} ${time}${tz}`.trim();
    if (dow === '*' || dow === '?') return `Daily ${time}${tz}`.trim();
    const label = _DOW_LABEL[dow] || dow.split(',').map((d) => _DOW_LABEL[d] || d).join(', ');
    return `${label} ${time}${tz}`.trim();
  }
  return expr;
}

/** Estimate fires per 30-day month for the points forecast (rough). */
export function firesPerMonth(cadence) {
  if (!cadence || typeof cadence !== 'object') return 0;
  const type = String(cadence.type || '').toUpperCase();
  const expr = String(cadence.expr || '');
  if (type === 'ONCE') return 1;
  if (type === 'RATE') {
    const m = /^rate\((\d+)\s+(\w+)\)$/.exec(expr);
    if (!m) return 0;
    const n = parseInt(m[1], 10);
    const unit = m[2];
    const perDay = unit.startsWith('minute') ? (1440 / n)
      : unit.startsWith('hour') ? (24 / n)
      : unit.startsWith('day') ? (1 / n) : 0;
    return Math.round(perDay * 30);
  }
  if (type === 'CRON') {
    const m = /^cron\((\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\)$/.exec(expr);
    if (!m) return 0;
    const [, , , dom, , dow] = m;
    if (dom !== '?' && dom !== '*') return 1; // monthly
    if (dow === '*' || dow === '?') return 30; // daily
    if (dow === 'MON-FRI') return 22; // weekdays
    const days = dow.split(',').filter(Boolean).length || 1;
    return days * 4; // weekly-ish
  }
  return 0;
}
